import { memo, useCallback, useEffect, useState } from "react";
import { GoogleMap, InfoWindow, MarkerClustererF, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import { createPointerIcon } from "./googleMap.helper";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "./googleMapsConfig";
import './googleMap.css'
import { useNavigate } from "react-router-dom";

// A lone search result would otherwise sit at the map's maximum zoom, which is
// too close to give the hotel any context.
const SINGLE_RESULT_ZOOM = 15;

// A nationwide search (a hotel brand, say) has results from Alaska to Florida,
// and fitting all of them zooms out to the whole continent, which is unusable.
// Stop at a regional view instead; results outside it are still pinned, and
// panning loads whatever else is in view.
const MIN_FIT_ZOOM = 5;

export default memo(function MapHotels({ locations, center, zoom = 6, containerStyleCustom = { width: "350px", height: "500px", }, city, onBoundsChange, fitTo }) {
    const [hovered, setHovered] = useState(null);
    const [map, setMap] = useState(null);
    const navigate = useNavigate();
    const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

    // Fires after every pan and zoom settles, so the parent can load the
    // hotels that are actually on screen instead of only the current page.
    const handleIdle = useCallback(() => {
        if (!map || !onBoundsChange) return;
        const bounds = map.getBounds();
        if (!bounds) return;
        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();
        onBoundsChange({
            north: northEast.lat(),
            south: southWest.lat(),
            east: northEast.lng(),
            west: southWest.lng(),
        });
    }, [map, onBoundsChange]);

    // Frame the search results on load, so a single match is zoomed to instead
    // of being a lone pin on a city-wide view. `fitTo` only changes when the
    // search itself changes, so this never yanks the map back while panning.
    useEffect(() => {
        if (!map || !Array.isArray(fitTo) || fitTo.length === 0) return;
        const bounds = new window.google.maps.LatLngBounds();
        fitTo.forEach((position) => bounds.extend(position));
        map.fitBounds(bounds, 48);

        // The clamp waits for "idle", not "bounds_changed": fitBounds fires
        // bounds_changed before the zoom has settled, so reading getZoom() there
        // sees the old value and the clamp silently does nothing.
        const listener = window.google.maps.event.addListenerOnce(map, "idle", () => {
            if (fitTo.length === 1) {
                map.setZoom(SINGLE_RESULT_ZOOM);
            } else if (map.getZoom() < MIN_FIT_ZOOM) {
                map.setZoom(MIN_FIT_ZOOM);
            }
        });
        return () => window.google.maps.event.removeListener(listener);
    }, [map, fitTo]);

    if (!isLoaded) return <div style={containerStyleCustom} />;

    const openHotel = (loc) => navigate(loc.route || `/hotelreview?hotelID=${loc.id}`);

    return (
        <>
            <GoogleMap
                mapContainerStyle={containerStyleCustom}
                center={center} zoom={zoom}
                options={{
                    zoomControl: true,
                    scrollwheel: true,
                    gestureHandling: "auto"
                }}
                onLoad={setMap}
                onIdle={handleIdle}
                key={center.lat}
            >
                {/* Dense areas return hundreds of pins, so they are clustered
                    and split apart as the user zooms in. */}
                <MarkerClustererF averageCenter gridSize={45}>
                    {(clusterer) => (
                        <>
                            {Array.isArray(locations) && locations.map((loc) => (
                                loc &&
                                <MarkerF
                                    key={loc.id} position={loc.position} clusterer={clusterer}
                                    onMouseOver={() => setHovered(loc.id)}
                                    onClick={() => openHotel(loc)}
                                >
                                    {hovered === loc.id && (
                                        <InfoWindow
                                            position={loc.position}
                                            options={{ disableAutoPan: true }}
                                            onCloseClick={() => setHovered(null)}
                                        >
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => openHotel(loc)}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter") openHotel(loc);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div style={{
                                                    fontSize: "13px", fontWeight: "600",
                                                    color: "#2c3e50", backgroundColor: "rgba(255, 255, 255, 0.95)",
                                                    padding: "8px 4px", borderRadius: "6px",
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)", overflow: "hidden",
                                                    maxWidth: "240px",
                                                }}>
                                                    <p style={{ margin: "0 0 8px", lineHeight: "1.3" }}>{loc.name}</p>
                                                    {loc.location && <span style={{ display: "block", fontSize: "12px", fontWeight: "400", color: "#555", lineHeight: "1.35", marginBottom: "6px" }}>{loc.location}</span>}
                                                    {loc.source === "google" && <span style={{ display: "block", fontSize: "11px", fontWeight: "500", color: "#6E6E6E", marginBottom: loc.rating ? "6px" : 0 }} translate="no">Google Maps</span>}
                                                    {loc.rating && <span style={{ display: "block", fontSize: "12px", fontWeight: "400", color: "#555" }}>Autie rating: {loc.rating}</span>}
                                                </div>
                                            </div>
                                        </InfoWindow>
                                    )}
                                </MarkerF>
                            ))}
                        </>
                    )}
                </MarkerClustererF>
                {city && locations?.length === 0 && <MarkerF key={city.id} position={{ lat: city.latitude, lng: city.longitude }} icon={createPointerIcon(city.stateAbbreviation ? `${city.name}, ${city.stateAbbreviation}` : city.name)}></MarkerF>}
            </GoogleMap>
        </>
    );
})
